import { FC, useEffect, useRef, useState } from "react";

import Table from "@components/Table/index";
import CustomModal from "@components/customModel";
import { IModal, IVersion } from "@types";
import {
  ErrorServices,
  useFetchLongTermIncentiveVersionsQuery,
  useUpdateLongTermIncentiveVersionMutation,
} from "@services";
import { getVersionsColumns } from "../config";
import { useTypedSelector } from "@hooks";

interface ILongTermIncentiveVersions extends IModal {
  download: (id?: number) => Promise<void>;
}

const LongTermIncentiveVersions: FC<ILongTermIncentiveVersions> = ({
  isVisible,
  setIsVisible,
  download,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [versionId, setVersionId] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadingVersionId = useRef<number | null>(null);

  const { company } = useTypedSelector(state => state.auth.user!);
  const { id: company_id } = company!;

  const { data: versions, isLoading } =
    useFetchLongTermIncentiveVersionsQuery({
      page,
      pageSize,
      company_id,
    });

  const [updateLongTermIncentiveVersion] =
    useUpdateLongTermIncentiveVersionMutation();

  const { data, pagination } = versions || {};

  useEffect(() => {
    if (data?.length) {
      const activeVersion = data.find(
        (version: IVersion) => version.is_active === "TRUE"
      );

      if (activeVersion?.id) {
        setVersionId(activeVersion.id);
      }
    }
  }, [data]);

  const handleActive = async (id: number) => {
    try {
      setVersionId(id);

      await updateLongTermIncentiveVersion({
        id,
        company_id,
      }).unwrap();
    } catch (error) {
      ErrorServices(error);
    }
  };

  const handleDownload = async (id?: number) => {
    try {
      setIsDownloading(true);
      downloadingVersionId.current = id ?? null;

      await download(id);
    } catch (error) {
      ErrorServices(error);
    } finally {
      setIsDownloading(false);
      downloadingVersionId.current = null;
    }
  };

  const versionsColumns = getVersionsColumns({
    handleActive,
    versionId,
    download: handleDownload,
    isDownloading,
    downloadingVersionId,
  });

  return (
    <CustomModal
      isVisible={isVisible}
      title="Previous Versions of Long Term Incentives"
      onClose={() => setIsVisible(false)}
      footer={null}
      width="w-3/4"
      height="80vh"
    >
      <Table
        isLoading={isLoading}
        data={data}
        columns={versionsColumns}
        pagination
        customStyles="mx-4"
        count={pagination?.count}
        onChangePage={setPage}
        onPageSizeChange={setPageSize}
        page={page}
      />
    </CustomModal>
  );
};

export default LongTermIncentiveVersions;